import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { ScopePicker, type Scope } from "../components/ScopePicker";
import { money } from "../lib/format";

interface AllTime {
  units: { bought: number; sold: number; onHand: number; writtenOff: number };
  revenue: number;
  shippingCollected: number;
  cogs: number;
  grossProfit: number;
  platformFees: number;
  processingFees: number;
  shippingLabels: number;
  packaging: number;
  otherCosts: number;
  costsByKind: Record<string, number>;
  netFromSales: number;
  writtenOff: number;
  netProfit: number;
  spent: number;
  onHandCost: number;
  unexpensedCosts: number;
  unexpensedItems: number;
  marginPct: number | null;
  grossMarginPct: number | null;
  roiPct: number | null;
  avgSalePrice: number | null;
  avgProfitPerSale: number | null;
  sellThroughPct: number | null;
  avgDaysToSale: number | null;
  firstPurchase: string | null;
  firstSale: string | null;
  lastSale: string | null;
}

interface MonthRow {
  month: string;
  boughtUnits: number;
  spent: number;
  soldUnits: number;
  revenue: number;
  shippingCollected: number;
  cogs: number;
  platformFees: number;
  processingFees: number;
  shippingLabels: number;
  packaging: number;
  otherCosts: number;
  writtenOff: number;
  writtenOffUnits: number;
  netProfit: number;
}

interface Report { scope: Scope; allTime: AllTime; months: MonthRow[] }

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (ym: string) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const pct = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? "—" : `${v.toFixed(0)}%`);
const ofRevenue = (v: number, revenue: number) => (revenue > 0 ? (v / revenue) * 100 : null);
const signed = (v: number) => (v < 0 ? "neg" : v > 0 ? "pos" : "");
// Date-only values (YYYY-MM-DD) would otherwise be read as UTC midnight and
// show the previous month in US time zones on the 1st.
const longDate = (iso: string | null) =>
  iso
    ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso)
        .toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "—";

/* ---------------------------------------------------------------------------
 * All-time statement
 * ------------------------------------------------------------------------- */

/** One line of the income statement. `sign` is how it enters the total;
 *  `share` is the line as a percentage of revenue. */
function Line({
  label, value, sign, hint, kind, share,
}: {
  label: string;
  value: number;
  sign?: "+" | "−";
  hint?: string;
  kind?: "subtotal" | "total";
  share?: number | null;
}) {
  return (
    <div className={`statement-row ${kind ?? ""}`}>
      <span className="lbl">
        {label}
        {hint && <span className="sub">{hint}</span>}
      </span>
      <span className={`val ${kind ? signed(value) : ""}`}>
        {sign && <span className="sign">{sign}</span>}
        {money(Math.abs(value))}
      </span>
      <span className="share">{share === undefined ? "" : pct(share)}</span>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="statement-row">
      <span className="lbl">{label}{hint && <span className="sub">{hint}</span>}</span>
      <span className="val">{value}</span>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Month-by-month: months across, P&L lines down
 * ------------------------------------------------------------------------- */

interface Col { label: string; r: MonthRow; kind: "all" | "year" | "month" }

function sumRows(rows: MonthRow[], month: string): MonthRow {
  const out: MonthRow = {
    month, boughtUnits: 0, spent: 0, soldUnits: 0, revenue: 0, shippingCollected: 0, cogs: 0,
    platformFees: 0, processingFees: 0, shippingLabels: 0, packaging: 0, otherCosts: 0,
    writtenOff: 0, writtenOffUnits: 0, netProfit: 0,
  };
  for (const r of rows) {
    for (const k of Object.keys(out) as Array<keyof MonthRow>) {
      if (k !== "month") (out[k] as number) += r[k] as number;
    }
  }
  return out;
}

const isEmpty = (r: MonthRow) => r.soldUnits === 0 && r.boughtUnits === 0 && r.writtenOffUnits === 0;

type LineKind = "money" | "count" | "pct";
interface PlLine {
  label: string;
  kind: LineKind;
  get: (r: MonthRow) => number | null;
  cls?: string;      // subtotal | total | pctline | section
  sign?: "+" | "−";
  hideIfZero?: boolean;
}

const contribution = (r: MonthRow) => r.revenue - r.cogs;
const PL_LINES: PlLine[] = [
  { label: "Items sold", kind: "count", get: (r) => r.soldUnits },
  { label: "Sales revenue", kind: "money", get: (r) => r.revenue },
  { label: "Cost of goods sold", kind: "money", get: (r) => r.cogs, sign: "−" },
  { label: "Gross contribution", kind: "money", get: contribution, cls: "subtotal" },
  { label: "Gross contribution %", kind: "pct", get: (r) => ofRevenue(contribution(r), r.revenue), cls: "pctline" },
  { label: "Shipping collected", kind: "money", get: (r) => r.shippingCollected, sign: "+", hideIfZero: true },
  { label: "Platform fees", kind: "money", get: (r) => r.platformFees, sign: "−" },
  { label: "Payment processing", kind: "money", get: (r) => r.processingFees, sign: "−" },
  { label: "Shipping labels", kind: "money", get: (r) => r.shippingLabels, sign: "−", hideIfZero: true },
  { label: "Packaging", kind: "money", get: (r) => r.packaging, sign: "−" },
  { label: "Other costs", kind: "money", get: (r) => r.otherCosts, sign: "−", hideIfZero: true },
  { label: "Written off", kind: "money", get: (r) => r.writtenOff, sign: "−" },
  { label: "Net profit", kind: "money", get: (r) => r.netProfit, cls: "total" },
  { label: "Net margin %", kind: "pct", get: (r) => ofRevenue(r.netProfit, r.revenue), cls: "pctline" },
  { label: "Cash into inventory", kind: "count", get: () => null, cls: "section" },
  { label: "Items bought", kind: "count", get: (r) => r.boughtUnits },
  { label: "Spent on inventory", kind: "money", get: (r) => r.spent },
];

function Cell({ line, col }: { line: PlLine; col: Col }) {
  const v = line.get(col.r);
  const quiet = isEmpty(col.r);
  if (line.cls === "section") return <td />;
  if (v == null || quiet || (v === 0 && line.kind !== "count")) return <td className="fin-zero">—</td>;
  if (line.kind === "count") return <td>{v}</td>;
  if (line.kind === "pct") return <td className={`pctcell ${signed(v)}`}>{pct(v)}</td>;
  const cls = line.cls === "subtotal" || line.cls === "total" ? signed(v) : "";
  return (
    <td className={cls}>
      {line.sign && <span className="sign">{line.sign}</span>}
      {money(Math.abs(v))}
    </td>
  );
}

function MonthTable({ months }: { months: MonthRow[] }) {
  // Columns: all time, then for each year (newest first) the year total and
  // its months (newest first). Months with nothing in them are left out.
  const cols = useMemo<Col[]>(() => {
    const active = months.filter((m) => !isEmpty(m));
    const byYear = new Map<string, MonthRow[]>();
    for (const m of active) {
      const y = m.month.slice(0, 4);
      byYear.set(y, [...(byYear.get(y) ?? []), m]);
    }
    const out: Col[] = [{ label: "All time", r: sumRows(active, "all"), kind: "all" }];
    for (const [year, rows] of [...byYear.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1))) {
      out.push({ label: year, r: sumRows(rows, year), kind: "year" });
      for (const r of [...rows].sort((a, b) => (a.month < b.month ? 1 : -1))) {
        out.push({ label: monthLabel(r.month), r, kind: "month" });
      }
    }
    return out;
  }, [months]);

  const lines = PL_LINES.filter(
    (l) => !l.hideIfZero || cols.some((c) => (l.get(c.r) ?? 0) !== 0)
  );

  return (
    <div className="fin-scroll">
      <table className="fin-table fin-vertical">
        <thead>
          <tr>
            <th />
            {cols.map((c, i) => <th key={i} className={c.kind}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.label} className={line.cls ?? ""}>
              <th scope="row">{line.label}</th>
              {cols.map((c, i) => <Cell key={i} line={line} col={c} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------------- */

export function Finance() {
  const [scope, setScope] = useState<Scope>("business");
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        const r = await api.get<Report>(`/finance?scope=${scope}`);
        if (!cancelled) setData(r);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load the numbers");
      }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  const a = data?.allTime;
  const grossContribution = a ? a.revenue - a.cogs : 0;
  const contributionPct = a ? ofRevenue(grossContribution, a.revenue) : null;
  const hasActivity = !!data && data.months.some((m) => !isEmpty(m));

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">money</span>
        {a && <span className="num" style={{ color: "var(--muted)" }}>{a.units.sold} sold</span>}
      </header>

      {error && <div className="notice">{error}</div>}

      <ScopePicker scope={scope} onChange={setScope} />

      {!a ? (
        !error && <p className="hint">Loading…</p>
      ) : (
        <>
          <section className="ledger">
            <div>
              <span className="eyebrow">Revenue</span>
              <span className="value">{money(a.revenue)}</span>
            </div>
            <div>
              <span className="eyebrow">Gross contribution</span>
              <span className={`value ${signed(grossContribution)}`}>{money(grossContribution)}</span>
              <span className="hint" style={{ margin: 0 }}>{pct(contributionPct)} of revenue</span>
            </div>
            <div>
              <span className="eyebrow">Net profit</span>
              <span className={`value ${signed(a.netProfit)}`}>{money(a.netProfit)}</span>
              <span className="hint" style={{ margin: 0 }}>{pct(a.marginPct)} of revenue</span>
            </div>
            <div>
              <span className="eyebrow">On hand, at cost</span>
              <span className="value">{money(a.onHandCost)}</span>
            </div>
          </section>

          <section className="rail">
            <h2 className="eyebrow rail-label">All time</h2>
            <div className="fin-grid">
              <div>
                <h3 className="fin-heading">Profit &amp; loss <span className="fin-heading-note">% of revenue</span></h3>
                <div className="statement">
                  <Line label="Sales revenue" value={a.revenue} hint={`${a.units.sold} sold`} share={a.revenue > 0 ? 100 : null} />
                  <Line label="Cost of goods sold" value={a.cogs} sign="−" hint="what you paid for what sold" share={ofRevenue(a.cogs, a.revenue)} />
                  <Line label="Gross contribution" value={grossContribution} kind="subtotal" share={contributionPct} />
                  {a.shippingCollected > 0 && (
                    <Line label="Shipping collected from buyers" value={a.shippingCollected} sign="+" share={ofRevenue(a.shippingCollected, a.revenue)} />
                  )}
                  <Line label="Platform fees" value={a.platformFees} sign="−" share={ofRevenue(a.platformFees, a.revenue)} />
                  <Line label="Payment processing" value={a.processingFees} sign="−" share={ofRevenue(a.processingFees, a.revenue)} />
                  {a.shippingLabels > 0 && (
                    <Line label="Shipping labels" value={a.shippingLabels} sign="−" share={ofRevenue(a.shippingLabels, a.revenue)} />
                  )}
                  {a.packaging > 0 && (
                    <Line label="Packaging" value={a.packaging} sign="−" share={ofRevenue(a.packaging, a.revenue)} />
                  )}
                  {a.otherCosts > 0 && (
                    <Line label="Other costs" value={a.otherCosts} sign="−" hint="mileage, laundering, repairs…" share={ofRevenue(a.otherCosts, a.revenue)} />
                  )}
                  <Line label="Net profit from sales" value={a.netFromSales} kind="subtotal" share={ofRevenue(a.netFromSales, a.revenue)} />
                  <Line
                    label="Written off"
                    value={a.writtenOff}
                    sign="−"
                    hint={`${a.units.writtenOff} damaged, donated or lost`}
                    share={ofRevenue(a.writtenOff, a.revenue)}
                  />
                  <Line label="Net profit" value={a.netProfit} kind="total" share={a.marginPct} />
                </div>
              </div>

              <div>
                <h3 className="fin-heading">Where the money is</h3>
                <div className="statement">
                  <Stat label="Spent on inventory, ever" value={money(a.spent)} hint={`${a.units.bought} items`} />
                  <Stat label="Still on hand, at cost" value={money(a.onHandCost)} hint={`${a.units.onHand} items`} />
                  {a.unexpensedCosts > 0 && (
                    <Stat
                      label="Costs on unsold items"
                      value={money(a.unexpensedCosts)}
                      hint={`${a.unexpensedItems} items · counted when they sell`}
                    />
                  )}
                  <Stat label="Sell-through" value={pct(a.sellThroughPct)} hint={`${a.units.sold} of ${a.units.bought} bought`} />
                  <Stat label="Return on cost" value={pct(a.roiPct)} hint="net from sales ÷ cost of goods" />
                  <Stat label="Average sale" value={money(a.avgSalePrice)} />
                  <Stat label="Average profit per sale" value={money(a.avgProfitPerSale)} />
                  <Stat label="Average days to sell" value={a.avgDaysToSale == null ? "—" : `${a.avgDaysToSale}`} hint="from listed to sold" />
                  <Stat label="Buying since" value={longDate(a.firstPurchase)} />
                  <Stat label="Selling since" value={longDate(a.firstSale)} hint={a.lastSale ? `last sale ${longDate(a.lastSale)}` : undefined} />
                </div>
              </div>
            </div>
            <p className="hint">
              Gross contribution is sales revenue less what you paid for the items that sold. Fees are computed
              from the platform cost profiles in Settings. A sale and everything it cost (the item, fees, label,
              packaging) count in the month the item sold.
            </p>
          </section>

          <section className="rail">
            <h2 className="eyebrow rail-label">Month by month</h2>
            {!hasActivity ? (
              <p className="hint">Nothing bought or sold yet.</p>
            ) : (
              <MonthTable months={data!.months} />
            )}
            <p className="hint">
              Newest month first; months with nothing bought or sold are left out. Items bought / spent are cash
              out in the month of purchase and become cost of goods when the items sell.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
